import { useState } from 'react';
import {
  DevHubBox,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubIconButton,
  DevHubLink,
  DevHubTypography,
  devhubMakeStyles,
  type DevHubTheme,
} from '@devxdiscover/devhub-ui';
import { LearnMoreButton } from './LearnMoreButton';
import { OnboardIdeaButton } from './OnboardIdeaButton';

/**
 * Styles for the hero banner
 */
const useHeroBannerStyles = devhubMakeStyles((theme: DevHubTheme) => ({
  heroBanner: {
    backgroundColor: '#000000',
    backgroundImage: 'url(/experimentation-banner-dark.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    filter: 'brightness(1.5)',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    position: 'relative',
  },
}));

const STORAGE_KEY = 'experimental-onboarding-hero-dismissed';

/**
 * Hero banner for experimental application onboarding
 */
export const ExperimentalOnboardingHero = () => {
  const classes = useHeroBannerStyles();

  // Initialize dismissal state from localStorage
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch (error) {
      // Gracefully handle localStorage errors (privacy mode, quota exceeded)
      return false;
    }
  });

  // Handle banner dismissal
  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
      setIsDismissed(true);
    } catch (error) {
      // Gracefully handle localStorage errors - still update state to dismiss
      setIsDismissed(true);
    }
  };

  // Early return if dismissed
  if (isDismissed) {
    return null;
  }

  return (
    <>
      <DevHubGrid container justifyContent="center" spacing={3}>
        <DevHubGrid container item xs={12}>
          <DevHubGrid item xs={12}>
            <DevHubBox
              color="white"
              padding="large"
              borderRadius="medium"
              textAlign="center"
              className={classes.heroBanner}
            >
              <DevHubIconButton
                onClick={handleDismiss}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: 'white',
                }}
                aria-label="Dismiss banner"
              >
                <DevHubDynamicIcon iconName="Clear" />
              </DevHubIconButton>
              <DevHubTypography variant="h3">
                DevX Experimental Application Onboarding
              </DevHubTypography>
              <DevHubBox padding="small" />
              <DevHubTypography variant="h6">
                Onboard an experimental application to try out an idea or Lab
                Week project
              </DevHubTypography>
              <DevHubTypography variant="body1" style={{ marginTop: '8px' }}>
                You get: GitHub + CNAP + Active Directory
              </DevHubTypography>
              <DevHubBox padding="small" />
              <DevHubTypography
                variant="body2"
                style={{
                  fontWeight: 500,
                  textAlign: 'center',
                }}
              >
                <DevHubDynamicIcon
                  iconName="Info"
                  style={{
                    fontSize: '1.2rem',
                    verticalAlign: 'middle',
                    marginRight: '8px',
                  }}
                />
                For production apps or full onboarding, go to{' '}
                <DevHubLink
                  href="/onboarding"
                  style={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    fontWeight: 500,
                  }}
                >
                  Onboarding
                </DevHubLink>
                .
              </DevHubTypography>
              <DevHubGrid
                container
                justifyContent="center"
                spacing={2}
                style={{ marginTop: '16px', gap: '4px', flexWrap: 'wrap' }}
              >
                <DevHubGrid item>
                  <LearnMoreButton
                    variant="contained"
                    size="large"
                    color="primary"
                  />
                </DevHubGrid>
                <DevHubGrid item>
                  <OnboardIdeaButton />
                </DevHubGrid>
              </DevHubGrid>
            </DevHubBox>
          </DevHubGrid>
        </DevHubGrid>
      </DevHubGrid>
      <DevHubBox padding="medium" />
    </>
  );
};
